/**
 * Pack Builder — RFID Tags collection interactive pack creator
 * Allows customers to build custom RFID tag packs with tiered discounts.
 *
 * All dynamic content rendered via innerHTML uses the esc() helper which
 * sanitizes via textContent assignment. All data originates from Liquid-rendered
 * JSON (server-side trusted data, not user input).
 */
(function () {
  'use strict';

  /* ── Config ──────────────────────────────────────────────────── */
  var translations = {};
  function translate(key, values) {
    var text = translations[key] || key;
    Object.keys(values || {}).forEach(function (name) { text = text.split('{'+name+'}').join(String(values[name])); });
    return text;
  }
  var STORAGE_KEY = 'lab401_pack_builder';
  var STORAGE_TTL = 24 * 60 * 60 * 1000; // 24 hours
  var PER_PAGE = 50;

  var DISCOUNT_RULES = {
    product_tiers: [
      { min_qty: 5, discount: 0.05 },
      { min_qty: 10, discount: 0.10 },
      { min_qty: 20, discount: 0.15 },
    ],
    accumulating_tiers: [
      { min_total: 10, discount: 0.05 },
      { min_total: 20, discount: 0.05 },
      { min_total: 30, discount: 0.05 },
    ],
  };

  /* Meta field display prefixes (keys match Liquid: filter.p.m.rfid.X → X) */
  var META_PREFIXES = {};

  /* Tier labels for line item property */
  var TIER_MAP = [
    { min: 20, tier: 'tier3' },
    { min: 10, tier: 'tier2' },
    { min: 5, tier: 'tier1' },
  ];

  /* ── State ───────────────────────────────────────────────────── */
  var allVariants = [];       // Full product data from Liquid JSON
  var filters = {};           // { filterKey: Set([values]) }
  var filterDefs = [];        // [{key, label, values:[{value,count}]}]
  var selections = {};        // { variantId: quantity }
  var currentPage = 1;
  var openFilterKey = null;
  var barExpanded = false;
  var lastDiscountTier = 0;   // For confetti trigger
  var viewMode = loadViewMode(); // 'grid' or 'table'

  /* ── Touch detection ────────────────────────────────────────── */
  function isTouchDevice() {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  /* ── Auto-build filters from variant meta ───────────────────── */
  var META_LABELS = {};

  function buildFiltersFromMeta(variants) {
    var keyValues = {}; // { key: { value: count } }
    variants.forEach(function (v) {
      if (!v.meta) return;
      Object.keys(v.meta).forEach(function (key) {
        var raw = v.meta[key];
        if (!raw) return;
        if (!keyValues[key]) keyValues[key] = {};
        // Handle comma-separated list values
        var vals = String(raw).split(',');
        vals.forEach(function (val) {
          val = val.trim();
          if (!val) return;
          keyValues[key][val] = (keyValues[key][val] || 0) + 1;
        });
      });
    });

    var defs = [];
    Object.keys(keyValues).forEach(function (key) {
      var valMap = keyValues[key];
      var values = Object.keys(valMap).sort().map(function (v) {
        return { value: v, count: valMap[v] };
      });
      if (values.length > 0) {
        defs.push({
          key: key,
          label: META_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }),
          values: values,
        });
      }
    });
    return defs;
  }

  /* ── Init ────────────────────────────────────────────────────── */
  function init() {
    var root = document.getElementById('pack-builder');
    if (!root) return;

    // Parse data embedded by Liquid
    var dataEl = document.getElementById('pb-data');
    if (!dataEl) return;
    try {
      var data = JSON.parse(dataEl.textContent);
      translations = data.translations || {};
      META_LABELS['frequency'] = translate('meta_frequency');
      META_LABELS['chipset'] = translate('meta_chipset');
      META_LABELS['uid_length'] = translate('meta_uid_length');
      META_LABELS['generation'] = translate('meta_generation');
      META_LABELS['memory_size'] = translate('meta_memory_size');
      META_LABELS['format'] = translate('meta_format');
      META_LABELS['iso_standard'] = translate('meta_iso_standard');
      META_LABELS['tool_compatibility'] = translate('meta_tool_compatibility');
      META_PREFIXES['frequency'] = translate('prefix_frequency');
      META_PREFIXES['uid_length'] = translate('prefix_uid_length');
      META_PREFIXES['format'] = translate('prefix_format');
      META_PREFIXES['generation'] = translate('prefix_generation');
      META_PREFIXES['memory_size'] = translate('prefix_memory_size');
      META_PREFIXES['chipset'] = translate('prefix_chipset');

      allVariants = data.variants || [];
      filterDefs = data.filters || [];
    } catch (e) {
      console.error('Pack Builder: Failed to parse data', e);
      return;
    }

    // Auto-build filter definitions from variant meta if none provided
    // (happens when storefront filtering is not enabled for RFID metafields)
    if (filterDefs.length === 0 && allVariants.length > 0) {
      filterDefs = buildFiltersFromMeta(allVariants);
    }

    // Add active class to hide default collection elements
    var outer = root.closest('.productgrid--outer');
    if (outer) outer.classList.add('pack-builder-active');

    // Apply touch class for mobile button visibility
    if (isTouchDevice()) {
      root.classList.add('pb-touch');
    }

    // Load selections from localStorage
    loadSelections();

    // Build filter state
    filterDefs.forEach(function (f) { filters[f.key] = new Set(); });

    // Render
    renderFilters();
    renderView();
    renderBar();

    // Close dropdowns on outside click
    document.addEventListener('click', function (e) {
      if (openFilterKey && !e.target.closest('.pb-filter')) {
        openFilterKey = null;
        renderFilters();
      }
    });
  }

  /* ── localStorage ────────────────────────────────────────────── */
  function loadSelections() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var data = JSON.parse(raw);
      if (Date.now() - data.timestamp > STORAGE_TTL) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      selections = data.selections || {};
    } catch (e) {
      selections = {};
    }
  }

  function saveSelections() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        selections: selections,
        timestamp: Date.now(),
      }));
    } catch (e) { /* quota exceeded */ }
  }

  function loadViewMode() {
    try {
      var v = localStorage.getItem('lab401_pack_view');
      return (v === 'table') ? 'table' : 'grid';
    } catch (e) { return 'grid'; }
  }

  function saveViewMode() {
    try { localStorage.setItem('lab401_pack_view', viewMode); }
    catch (e) { /* quota exceeded */ }
  }

  /* ── Filtering ───────────────────────────────────────────────── */

  /**
   * Check if a meta value matches any of the selected filter values.
   * Meta values may be comma-separated lists (from list-type metafields).
   */
  function metaMatches(metaVal, selectedSet) {
    if (!metaVal) return false;
    if (selectedSet.has(metaVal)) return true;
    // Check if any comma-separated part matches
    if (metaVal.indexOf(',') !== -1) {
      var parts = metaVal.split(',');
      for (var i = 0; i < parts.length; i++) {
        if (selectedSet.has(parts[i].trim())) return true;
      }
    }
    return false;
  }

  function getFilteredVariants() {
    return allVariants.filter(function (v) {
      for (var key in filters) {
        var selected = filters[key];
        if (selected.size === 0) continue;
        var val = (v.meta && v.meta[key]) || '';
        if (!metaMatches(val, selected)) return false;
      }
      return true;
    });
  }

  function getFilterCounts(filterKey) {
    var counts = {};
    allVariants.forEach(function (v) {
      var pass = true;
      for (var key in filters) {
        if (key === filterKey) continue;
        var selected = filters[key];
        if (selected.size === 0) continue;
        var val = (v.meta && v.meta[key]) || '';
        if (!metaMatches(val, selected)) { pass = false; break; }
      }
      if (pass) {
        var val = (v.meta && v.meta[filterKey]) || '';
        // For comma-separated values, count each individual value
        if (val && val.indexOf(',') !== -1) {
          var parts = val.split(',');
          for (var i = 0; i < parts.length; i++) {
            var p = parts[i].trim();
            if (p) counts[p] = (counts[p] || 0) + 1;
          }
        } else if (val) {
          counts[val] = (counts[val] || 0) + 1;
        }
      }
    });
    return counts;
  }

  /* ── Discount calculation ────────────────────────────────────── */
  function calcDiscounts() {
    var items = [];
    var totalQty = 0;
    var subtotal = 0;

    for (var vid in selections) {
      var qty = selections[vid];
      if (qty <= 0) continue;
      var v = allVariants.find(function (x) { return String(x.id) === String(vid); });
      if (!v) continue;

      // Per-product tier
      var prodDiscount = 0;
      for (var i = DISCOUNT_RULES.product_tiers.length - 1; i >= 0; i--) {
        if (qty >= DISCOUNT_RULES.product_tiers[i].min_qty) {
          prodDiscount = DISCOUNT_RULES.product_tiers[i].discount;
          break;
        }
      }

      var lineTotal = v.price * qty;
      var lineSaving = lineTotal * prodDiscount;

      items.push({
        id: vid,
        title: v.title,
        qty: qty,
        unitPrice: v.price,
        prodDiscount: prodDiscount,
        lineTotal: lineTotal,
        lineSaving: lineSaving,
      });

      totalQty += qty;
      subtotal += lineTotal;
    }

    // Accumulating bulk discount
    var bulkDiscount = 0;
    DISCOUNT_RULES.accumulating_tiers.forEach(function (tier) {
      if (totalQty >= tier.min_total) bulkDiscount += tier.discount;
    });

    var afterProdDiscount = 0;
    items.forEach(function (item) {
      afterProdDiscount += item.lineTotal - item.lineSaving;
    });
    var bulkSaving = afterProdDiscount * bulkDiscount;
    var productSaving = 0;
    items.forEach(function (item) { productSaving += item.lineSaving; });
    var totalSaving = productSaving + bulkSaving;

    var finalTotal = subtotal - totalSaving;

    var tierCount = 0;
    DISCOUNT_RULES.accumulating_tiers.forEach(function (tier) {
      if (totalQty >= tier.min_total) tierCount++;
    });
    items.forEach(function (item) {
      if (item.prodDiscount > 0) tierCount++;
    });

    return {
      items: items,
      totalQty: totalQty,
      subtotal: subtotal,
      bulkDiscount: bulkDiscount,
      bulkSaving: bulkSaving,
      productSaving: productSaving,
      totalSaving: totalSaving,
      finalTotal: finalTotal,
      tierCount: tierCount,
    };
  }

  function getNextMilestone(totalQty) {
    for (var i = 0; i < DISCOUNT_RULES.accumulating_tiers.length; i++) {
      var min = DISCOUNT_RULES.accumulating_tiers[i].min_total;
      if (totalQty < min) {
        return { needed: min - totalQty, discount: DISCOUNT_RULES.accumulating_tiers[i].discount };
      }
    }
    return null;
  }

  /* ── Render helpers (safe HTML builders) ─────────────────────── */

  /**
   * Escapes a string for safe HTML insertion.
   * Uses DOM textContent for robust entity encoding.
   */
  var _escDiv = document.createElement('div');
  function esc(str) {
    if (!str) return '';
    _escDiv.textContent = str;
    return _escDiv.innerHTML;
  }

  /**
   * Splits a title into first line (max chars) and second line (remainder).
   * Mirrors the Liquid logic in product-card-relaxed.liquid.
   */
  function splitTitle(title, maxFirst) {
    if (!title) return { first: '', second: '' };
    var words = title.split(' ');
    var first = '';
    var second = '';
    var onSecond = false;
    for (var i = 0; i < words.length; i++) {
      if (!onSecond) {
        var test = first ? first + ' ' + words[i] : words[i];
        if (test.length <= maxFirst) {
          first = test;
        } else if (!first) {
          first = words[i];
        } else {
          onSecond = true;
          second = words[i];
        }
      } else {
        second += ' ' + words[i];
      }
    }
    return { first: first, second: second };
  }

  function truncate(str, len) {
    if (!str) return '';
    return str.length > len ? str.slice(0, len) + '\u2026' : str;
  }

  function formatMoney(cents) {
    var fmt = window.LAB401_MONEY_FORMAT || '\u20ac{{amount}}';
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = Math.round(cents / 100).toString();
    var amountWithComma = amount.replace('.', ',');
    return fmt
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  /* SVG constants (static, no user data) */
  var SVG_CHEVRON = '<svg class="pb-filter__chevron" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';
  var SVG_CHECK = '<svg viewBox="0 0 10 8"><path d="M1 4l2.5 3L9 1" stroke="#fff" stroke-width="1.5" fill="none"/></svg>';
  var SVG_EXPAND = '<svg class="pb-bar__expand-icon" viewBox="0 0 20 20"><path d="M5 12l5-5 5 5" stroke="#fff" stroke-width="2" fill="none"/></svg>';

  /* ── Render: Filters ─────────────────────────────────────────── */
  function renderFilters() {
    var el = document.getElementById('pb-filters');
    if (!el) return;

    var parts = [];
    filterDefs.forEach(function (def) {
      var selected = filters[def.key];
      var isOpen = openFilterKey === def.key;
      var hasSelection = selected && selected.size > 0;
      var counts = getFilterCounts(def.key);

      var h = '<div class="pb-filter' + (isOpen ? ' pb-filter--open' : '') + '" data-filter-key="' + esc(def.key) + '">';
      h += '<button class="pb-filter__pill' + (hasSelection ? ' pb-filter__pill--active' : '') + '" data-pb-filter-toggle="' + esc(def.key) + '">';
      h += esc(def.label);

      if (hasSelection) {
        selected.forEach(function (val) {
          h += ' <span class="pb-filter__tag">' + esc(truncate(val, 16));
          h += ' <span class="pb-filter__tag-x" data-pb-filter-remove="' + esc(def.key) + '|' + esc(val) + '">\u00d7</span></span>';
        });
      }

      h += ' ' + SVG_CHEVRON;
      h += '</button>';

      h += '<div class="pb-filter__dropdown">';
      def.values.forEach(function (opt) {
        var isSel = selected && selected.has(opt.value);
        var count = counts[opt.value] || 0;
        h += '<div class="pb-filter__option' + (isSel ? ' pb-filter__option--selected' : '') + '" data-pb-filter-select="' + esc(def.key) + '|' + esc(opt.value) + '">';
        h += '<span class="pb-filter__option-check">' + SVG_CHECK + '</span>';
        h += '<span>' + esc(opt.value) + '</span>';
        h += '<span class="pb-filter__option-count">' + count + '</span>';
        h += '</div>';
      });
      h += '</div></div>';
      parts.push(h);
    });

    var anyActive = false;
    for (var k in filters) { if (filters[k].size > 0) { anyActive = true; break; } }
    if (anyActive) {
      parts.push('<button class="pb-filters__reset" data-pb-filters-reset>' + esc(translate('reset_filters')) + '</button>');
    }

    // View toggle (grid/table)
    var SVG_GRID = '<svg viewBox="0 0 16 16" width="14" height="14"><rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor"/><rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor"/><rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor"/><rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor"/></svg>';
    var SVG_TABLE = '<svg viewBox="0 0 16 16" width="14" height="14"><rect x="1" y="1" width="14" height="3" rx="1" fill="currentColor"/><rect x="1" y="6" width="14" height="3" rx="1" fill="currentColor"/><rect x="1" y="11" width="14" height="3" rx="1" fill="currentColor"/></svg>';
    parts.push('<div class="pb-view-toggle">' +
      '<button class="pb-view-toggle__btn' + (viewMode === 'grid' ? ' pb-view-toggle__btn--active' : '') + '" data-pb-view="grid" title="' + esc(translate('grid_view')) + '">' + SVG_GRID + '</button>' +
      '<button class="pb-view-toggle__btn' + (viewMode === 'table' ? ' pb-view-toggle__btn--active' : '') + '" data-pb-view="table" title="' + esc(translate('table_view')) + '">' + SVG_TABLE + '</button>' +
      '</div>');

    el.innerHTML = parts.join('');
    bindFilterEvents(el);
  }

  function bindFilterEvents(el) {
    el.querySelectorAll('[data-pb-filter-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var key = btn.getAttribute('data-pb-filter-toggle');
        openFilterKey = openFilterKey === key ? null : key;
        renderFilters();
      });
    });

    el.querySelectorAll('[data-pb-filter-select]').forEach(function (opt) {
      opt.addEventListener('click', function (e) {
        e.stopPropagation();
        var raw = opt.getAttribute('data-pb-filter-select');
        var idx = raw.indexOf('|');
        var key = raw.substring(0, idx);
        var val = raw.substring(idx + 1);
        if (filters[key].has(val)) {
          filters[key].delete(val);
        } else {
          filters[key].add(val);
        }
        currentPage = 1;
        renderFilters();
        renderView();
      });
    });

    el.querySelectorAll('[data-pb-filter-remove]').forEach(function (x) {
      x.addEventListener('click', function (e) {
        e.stopPropagation();
        var raw = x.getAttribute('data-pb-filter-remove');
        var idx = raw.indexOf('|');
        var key = raw.substring(0, idx);
        var val = raw.substring(idx + 1);
        filters[key].delete(val);
        currentPage = 1;
        renderFilters();
        renderView();
      });
    });

    var resetBtn = el.querySelector('[data-pb-filters-reset]');
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        for (var k in filters) filters[k].clear();
        currentPage = 1;
        openFilterKey = null;
        renderFilters();
        renderView();
      });
    }

    // View toggle
    el.querySelectorAll('[data-pb-view]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var mode = btn.getAttribute('data-pb-view');
        if (mode === viewMode) return;
        viewMode = mode;
        saveViewMode();
        renderView();
      });
    });
  }

  /* ── View mode dispatch ─────────────────────────────────────── */
  function renderView() {
    if (viewMode === 'table') {
      renderTable();
    } else {
      renderGrid();
    }
    updateViewToggle();
  }

  function updateViewToggle() {
    var gridBtn = document.querySelector('[data-pb-view="grid"]');
    var tableBtn = document.querySelector('[data-pb-view="table"]');
    if (gridBtn) gridBtn.classList.toggle('pb-view-toggle__btn--active', viewMode === 'grid');
    if (tableBtn) tableBtn.classList.toggle('pb-view-toggle__btn--active', viewMode === 'table');
  }

  /* ── Render: Table (compact list view) ─────────────────────── */
  function renderTable() {
    var gridEl = document.getElementById('pb-grid');
    var pagEl = document.getElementById('pb-pagination');
    if (!gridEl) return;

    var filtered = getFilteredVariants();
    var totalPages = Math.ceil(filtered.length / PER_PAGE) || 1;
    if (currentPage > totalPages) currentPage = totalPages;

    var start = (currentPage - 1) * PER_PAGE;
    var pageItems = filtered.slice(start, start + PER_PAGE);

    if (pageItems.length === 0) {
      gridEl.innerHTML = '<div class="pb-no-results">' + esc(translate('no_results')) + '</div>';
      if (pagEl) pagEl.innerHTML = '';
      return;
    }

    var isWide = window.innerWidth >= 1024;
    var h = '<table class="pb-table"><thead><tr>';
    h += '<th class="pb-table__th-img"></th>';
    h += '<th class="pb-table__th-title">' + esc(translate('title')) + '</th>';
    h += isWide ? '<th>' + esc(translate('price')) + '</th><th>' + esc(translate('features')) + '</th>' : '<th>' + esc(translate('features')) + '</th><th>' + esc(translate('price')) + '</th>';
    h += '<th class="pb-table__th-qty">' + esc(translate('quantity')) + '</th>';
    h += '</tr></thead><tbody>';

    pageItems.forEach(function (v) {
      var qty = selections[v.id] || 0;
      var selCls = qty > 0 ? ' pb-table__row--selected' : '';

      h += '<tr class="pb-table__row' + selCls + '" data-pb-variant="' + esc(String(v.id)) + '">';

      // Thumbnail
      h += '<td class="pb-table__cell-img">';
      if (v.image) {
        h += '<img class="pb-table__thumb" src="' + esc(v.image) + '" alt="' + esc(v.title) + '" loading="lazy">';
      }
      h += '</td>';

      // Title (linked)
      h += '<td class="pb-table__cell-title"><a href="' + esc(v.url) + '" target="_blank" class="pb-table__link">' + esc(v.title) + '</a></td>';

      // Build features cell
      var featHtml = '<td class="pb-table__cell-features">';
      if (v.meta) {
        for (var mk in v.meta) {
          if (!v.meta[mk]) continue;
          if (mk === 'tool_compatibility') continue;
          var prefix = META_PREFIXES[mk] || '';
          var vals = v.meta[mk].indexOf(',') !== -1 ? v.meta[mk].split(',') : [v.meta[mk]];
          for (var vi = 0; vi < vals.length; vi++) {
            var pv = vals[vi].trim();
            if (pv) {
              featHtml += '<span class="pb-table__feature">';
              if (prefix) featHtml += '<span class="pb-table__feature-label">' + esc(prefix) + '</span>';
              featHtml += esc(pv) + '</span>';
            }
          }
        }
      }
      featHtml += '</td>';

      // Build price cell
      var priceHtml = '<td class="pb-table__cell-price">' + formatMoney(v.price) + '</td>';

      // Wide: Price then Features; narrow: Features then Price
      h += isWide ? priceHtml + featHtml : featHtml + priceHtml;

      // Qty (accessories-style Add → qty selector)
      h += '<td class="pb-table__cell-qty">';
      h += '<button class="pb-table__add-btn" data-pb-add="' + v.id + '"' + (qty > 0 ? ' style="display:none"' : '') + '>' + esc(translate('add')) + '</button>';
      h += '<div class="pb-table__qty-controls"' + (qty > 0 ? '' : ' style="display:none"') + '>';
      h += '<button class="pb-table__qty-btn" data-pb-dec="' + v.id + '">\u2212</button>';
      h += '<span class="pb-table__qty-val">' + qty + '</span>';
      h += '<button class="pb-table__qty-btn" data-pb-inc="' + v.id + '">+</button>';
      h += '</div>';
      h += '</td>';

      h += '</tr>';
    });

    h += '</tbody></table>';
    gridEl.innerHTML = h;
    bindTableEvents(gridEl);

    // Pagination (same as grid)
    if (!pagEl) return;
    if (totalPages <= 1) { pagEl.innerHTML = ''; return; }
    var pParts = [];
    if (currentPage > 1) {
      pParts.push('<button class="pb-pagination__btn" data-pb-page="' + (currentPage - 1) + '">\u2039</button>');
    }
    for (var p = 1; p <= totalPages; p++) {
      pParts.push('<button class="pb-pagination__btn' + (p === currentPage ? ' pb-pagination__btn--active' : '') + '" data-pb-page="' + p + '">' + p + '</button>');
    }
    if (currentPage < totalPages) {
      pParts.push('<button class="pb-pagination__btn" data-pb-page="' + (currentPage + 1) + '">\u203a</button>');
    }
    pagEl.innerHTML = pParts.join('');
    pagEl.querySelectorAll('[data-pb-page]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        currentPage = parseInt(btn.getAttribute('data-pb-page'), 10);
        renderView();
        var gridTop = gridEl.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: gridTop, behavior: 'smooth' });
      });
    });
  }

  function bindTableEvents(el) {
    el.querySelectorAll('[data-pb-add]').forEach(function (btn) {
      btn.addEventListener('click', guarded(function () {
        setQty(btn.getAttribute('data-pb-add'), 1);
      }));
    });
    el.querySelectorAll('[data-pb-inc]').forEach(function (btn) {
      btn.addEventListener('click', guarded(function () {
        var id = btn.getAttribute('data-pb-inc');
        setQty(id, (selections[id] || 0) + 1);
      }));
    });
    el.querySelectorAll('[data-pb-dec]').forEach(function (btn) {
      btn.addEventListener('click', guarded(function () {
        var id = btn.getAttribute('data-pb-dec');
        setQty(id, (selections[id] || 0) - 1);
      }));
    });
  }

  /* ── Render: Grid ────────────────────────────────────────────── */
  function renderGrid() {
    var gridEl = document.getElementById('pb-grid');
    var pagEl = document.getElementById('pb-pagination');
    if (!gridEl) return;

    var filtered = getFilteredVariants();
    var totalPages = Math.ceil(filtered.length / PER_PAGE) || 1;
    if (currentPage > totalPages) currentPage = totalPages;

    var start = (currentPage - 1) * PER_PAGE;
    var pageItems = filtered.slice(start, start + PER_PAGE);

    if (pageItems.length === 0) {
      gridEl.innerHTML = '<div class="pb-no-results">' + esc(translate('no_results')) + '</div>';
      if (pagEl) pagEl.innerHTML = '';
      return;
    }

    var cards = [];
    pageItems.forEach(function (v) {
      var qty = selections[v.id] || 0;
      var selCls = qty > 0 ? ' pb-card--selected' : '';

      var c = '<div class="pb-card' + selCls + '" data-pb-variant="' + esc(String(v.id)) + '">';
      c += '<div class="pb-card__qty-badge">' + esc(translate('in_cart', {count:qty})) + '</div>';
      c += '<div class="pb-card__img-wrap">';
      if (v.image) {
        c += '<img class="pb-card__img" src="' + esc(v.image) + '" alt="' + esc(v.title) + '" loading="lazy">';
      }
      // Mobile/touch: inline add/qty + view buttons centered on thumbnail
      c += '<div class="pb-card__mobile-add">';
      c += '<button class="pb-card__add-btn" data-pb-add="' + v.id + '">' + esc(translate('add_to_pack')) + '</button>';
      c += '<div class="pb-card__qty-controls">';
      c += '<button class="pb-card__qty-btn" data-pb-dec="' + v.id + '">\u2212</button>';
      c += '<span class="pb-card__qty-val">' + qty + '</span>';
      c += '<button class="pb-card__qty-btn" data-pb-inc="' + v.id + '">+</button>';
      c += '</div>';
      c += '<a class="pb-card__view-btn" href="' + esc(v.url) + '">' + esc(translate('view_product')) + '</a>';
      c += '</div>';
      c += '</div>';

      // Desktop: hover overlay
      c += '<div class="pb-card__overlay">';
      c += '<button class="pb-card__add-btn" data-pb-add="' + v.id + '">' + esc(translate('add_to_pack')) + '</button>';
      c += '<div class="pb-card__qty-controls">';
      c += '<button class="pb-card__qty-btn" data-pb-dec="' + v.id + '">\u2212</button>';
      c += '<span class="pb-card__qty-val">' + qty + '</span>';
      c += '<button class="pb-card__qty-btn" data-pb-inc="' + v.id + '">+</button>';
      c += '</div>';
      c += '<a class="pb-card__view-btn" href="' + esc(v.url) + '">' + esc(translate('view_product')) + '</a>';
      c += '</div>';

      // Info (relaxed layout: title + price on same row, overflow to line 2)
      var titleParts = splitTitle(v.title, 18);
      c += '<div class="pb-card__info">';
      c += '<div class="pb-card__title-price-row">';
      c += '<div class="pb-card__title" title="' + esc(v.title) + '">' + esc(titleParts.first) + '</div>';
      c += '<div class="pb-card__price">' + formatMoney(v.price) + '</div>';
      c += '</div>';
      if (titleParts.second) {
        c += '<div class="pb-card__title-line2">' + esc(titleParts.second) + '</div>';
      }
      if (v.meta) {
        c += '<div class="pb-card__pills">';
        for (var mk in v.meta) {
          if (!v.meta[mk]) continue;
          if (mk === 'tool_compatibility') continue;
          var prefix = META_PREFIXES[mk] || '';
          var vals = v.meta[mk].indexOf(',') !== -1 ? v.meta[mk].split(',') : [v.meta[mk]];
          for (var vi = 0; vi < vals.length; vi++) {
            var pv = vals[vi].trim();
            if (pv) c += '<span class="pb-card__pill">' + esc(prefix + truncate(pv, 18)) + '</span>';
          }
        }
        c += '</div>';
      }
      c += '</div></div>';
      cards.push(c);
    });

    gridEl.innerHTML = cards.join('');
    bindGridEvents(gridEl);

    // Pagination
    if (!pagEl) return;
    if (totalPages <= 1) { pagEl.innerHTML = ''; return; }

    var pParts = [];
    if (currentPage > 1) {
      pParts.push('<button class="pb-pagination__btn" data-pb-page="' + (currentPage - 1) + '">\u2039</button>');
    }
    for (var p = 1; p <= totalPages; p++) {
      pParts.push('<button class="pb-pagination__btn' + (p === currentPage ? ' pb-pagination__btn--active' : '') + '" data-pb-page="' + p + '">' + p + '</button>');
    }
    if (currentPage < totalPages) {
      pParts.push('<button class="pb-pagination__btn" data-pb-page="' + (currentPage + 1) + '">\u203a</button>');
    }
    pagEl.innerHTML = pParts.join('');
    pagEl.querySelectorAll('[data-pb-page]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        currentPage = parseInt(btn.getAttribute('data-pb-page'), 10);
        renderView();
        var gridTop = gridEl.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: gridTop, behavior: 'smooth' });
      });
    });
  }

  /* Click guard — prevents double-fire on touch devices where a single
     tap can produce two click events (touch→click timing quirks).
     Guard is per-element so clicking Add then Inc (different buttons)
     is not blocked. */
  function guarded(fn) {
    return function (e) {
      e.stopPropagation();
      var btn = e.currentTarget;
      if (btn._pbGuard) return;
      btn._pbGuard = true;
      fn(e);
      setTimeout(function () { btn._pbGuard = false; }, 100);
    };
  }

  function bindGridEvents(el) {
    el.querySelectorAll('[data-pb-add]').forEach(function (btn) {
      btn.addEventListener('click', guarded(function () {
        setQty(btn.getAttribute('data-pb-add'), 1);
      }));
    });

    el.querySelectorAll('[data-pb-inc]').forEach(function (btn) {
      btn.addEventListener('click', guarded(function () {
        var id = btn.getAttribute('data-pb-inc');
        setQty(id, (selections[id] || 0) + 1);
      }));
    });

    el.querySelectorAll('[data-pb-dec]').forEach(function (btn) {
      btn.addEventListener('click', guarded(function () {
        var id = btn.getAttribute('data-pb-dec');
        setQty(id, (selections[id] || 0) - 1);
      }));
    });
  }

  function setQty(variantId, qty) {
    if (qty <= 0) {
      delete selections[variantId];
    } else {
      selections[variantId] = qty;
    }
    saveSelections();

    var newCalc = calcDiscounts();
    if (newCalc.tierCount > lastDiscountTier && lastDiscountTier > 0) {
      fireConfetti();
    }
    lastDiscountTier = newCalc.tierCount;

    // Patch only the affected card instead of rebuilding the entire grid
    updateCard(variantId);
    renderBar();
  }

  /**
   * Updates a single card in-place without re-rendering the full grid.
   * This prevents image reload flicker on quantity changes.
   */
  function updateCard(variantId) {
    var qty = selections[variantId] || 0;

    // Grid card update
    var card = document.querySelector('.pb-card[data-pb-variant="' + variantId + '"]');
    if (card) {
      if (qty > 0) {
        card.classList.add('pb-card--selected');
      } else {
        card.classList.remove('pb-card--selected');
      }
      var badge = card.querySelector('.pb-card__qty-badge');
      if (badge) badge.textContent = translate('in_cart', {count:qty});
      card.querySelectorAll('.pb-card__qty-val').forEach(function (el) {
        el.textContent = qty;
      });
    }

    // Table row update
    var row = document.querySelector('.pb-table__row[data-pb-variant="' + variantId + '"]');
    if (row) {
      if (qty > 0) {
        row.classList.add('pb-table__row--selected');
      } else {
        row.classList.remove('pb-table__row--selected');
      }
      var addBtn = row.querySelector('.pb-table__add-btn');
      var qtyControls = row.querySelector('.pb-table__qty-controls');
      var qtyVal = row.querySelector('.pb-table__qty-val');
      if (addBtn) addBtn.style.display = qty > 0 ? 'none' : '';
      if (qtyControls) qtyControls.style.display = qty > 0 ? 'flex' : 'none';
      if (qtyVal) qtyVal.textContent = qty;
    }
  }

  /* ── Render: Bottom bar ──────────────────────────────────────── */
  function renderBar() {
    var el = document.getElementById('pb-bar');
    if (!el) return;

    var d = calcDiscounts();
    if (d.totalQty === 0) {
      el.classList.remove('pb-bar--visible');
      return;
    }
    el.classList.add('pb-bar--visible');

    var milestone = getNextMilestone(d.totalQty);
    var gamifyMsg = '';
    if (milestone) {
      gamifyMsg = translate(milestone.needed === 1 ? 'reward_one' : 'reward_other', {count:milestone.needed, percent:Math.round(milestone.discount * 100)});
    }

    var summaryEl = el.querySelector('.pb-bar__summary');
    if (summaryEl) {
      var s = '';
      s += '<div class="pb-bar__stats-group">';
      s += '<div class="pb-bar__stat"><strong>' + d.totalQty + '</strong> ' + esc(translate(d.totalQty === 1 ? 'product_one' : 'product_other')) + '</div>';
      s += '<span class="pb-bar__details-pill" data-pb-toggle-detail>' + esc(translate('details')) + '</span>';
      s += '<div class="pb-bar__stat">' + esc(translate('total')) + ': <strong>' + formatMoney(d.finalTotal) + '</strong></div>';
      if (d.totalSaving > 0) {
        s += '<div class="pb-bar__stat pb-bar__discount">' + esc(translate('total_discount')) + ': <strong>' + formatMoney(d.totalSaving) + '</strong></div>';
      }
      s += '</div>';
      s += '<div class="pb-bar__actions">';
      s += '<button class="pb-bar__confirm" data-pb-confirm>' + esc(translate('confirm_pack')) + '</button>';
      s += '<button class="pb-bar__reset" data-pb-reset-pack>' + esc(translate('reset')) + '</button>';
      s += '</div>';
      if (gamifyMsg) s += '<div class="pb-bar__gamify">' + esc(gamifyMsg) + '</div>';
      summaryEl.innerHTML = s;
    }

    // Detail table
    var detailEl = el.querySelector('.pb-bar__detail');
    if (detailEl) {
      var bulkPct = d.bulkDiscount > 0 ? Math.round(d.bulkDiscount * 100) : 0;
      var t = '<table class="pb-bar__table">';
      t += '<thead><tr><th>' + esc(translate('product')) + '</th><th>' + esc(translate('quantity')) + '</th><th>' + esc(translate('unit')) + '</th><th>' + esc(translate('discount')) + '</th><th>' + esc(translate('subtotal')) + '</th></tr></thead><tbody>';
      d.items.forEach(function (item) {
        var afterProd = item.lineTotal - item.lineSaving;
        var afterAll = afterProd * (1 - d.bulkDiscount);
        var prodPct = item.prodDiscount > 0 ? Math.round(item.prodDiscount * 100) : 0;
        var discountParts = [];
        if (prodPct > 0) discountParts.push(prodPct + '%');
        if (bulkPct > 0) discountParts.push(bulkPct + '%');
        var discountLabel = discountParts.length > 0 ? discountParts.join(' + ') : '\u2014';
        t += '<tr><td class="pb-bar__cell-product">' + esc(item.title) + '</td>';
        t += '<td>' + item.qty + '</td>';
        t += '<td>' + formatMoney(item.unitPrice) + '</td>';
        t += '<td class="pb-bar__row-discount">' + discountLabel + '</td>';
        t += '<td>' + formatMoney(afterAll) + '</td></tr>';
      });
      t += '</tbody><tfoot>';
      t += '<tr><td colspan="4">' + esc(translate('total')) + '</td><td>' + formatMoney(d.finalTotal) + '</td></tr>';
      if (d.productSaving > 0) {
        t += '<tr><td colspan="4">' + esc(translate('product_discounts')) + '</td>';
        t += '<td class="pb-bar__row-discount">' + formatMoney(d.productSaving) + '</td></tr>';
      }
      if (d.bulkDiscount > 0) {
        t += '<tr><td colspan="4">' + esc(translate('bulk_bonus', {count:d.totalQty, percent:bulkPct})) + '</td>';
        t += '<td class="pb-bar__row-discount">' + formatMoney(d.bulkSaving) + '</td></tr>';
      }
      t += '</tfoot></table>';
      detailEl.innerHTML = t;
    }

    // Events
    var confirmBtn = el.querySelector('[data-pb-confirm]');
    if (confirmBtn) confirmBtn.onclick = confirmPack;

    var resetBtn = el.querySelector('[data-pb-reset-pack]');
    if (resetBtn) {
      resetBtn.onclick = function () {
        if (confirm(translate('reset_prompt'))) {
          selections = {};
          saveSelections();
          lastDiscountTier = 0;
          renderView();
          renderBar();
        }
      };
    }

    // Details pill toggles expanded view
    var detailsPill = el.querySelector('[data-pb-toggle-detail]');
    if (detailsPill) {
      detailsPill.onclick = function (e) {
        e.stopPropagation();
        barExpanded = !barExpanded;
        el.classList.toggle('pb-bar--expanded', barExpanded);
      };
    }
  }

  /* ── Confirm: Add to cart ────────────────────────────────────── */
  function confirmPack() {
    var d = calcDiscounts();
    if (d.items.length === 0) return;

    var items = d.items.map(function (item) {
      var tier = '';
      for (var i = 0; i < TIER_MAP.length; i++) {
        if (item.qty >= TIER_MAP[i].min) { tier = TIER_MAP[i].tier; break; }
      }
      var props = { _pack: 'true' };
      if (tier) props._pack_tier = tier;
      return { id: parseInt(item.id, 10), quantity: item.qty, properties: props };
    });

    var btn = document.querySelector('[data-pb-confirm]');
    if (btn) { btn.disabled = true; btn.textContent = translate('adding'); }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart error ' + res.status);
        return res.json();
      })
      .then(function () {
        selections = {};
        saveSelections();
        lastDiscountTier = 0;
        renderView();
        renderBar();
        if (btn) { btn.disabled = false; btn.textContent = translate('confirm_pack'); }
      })
      .catch(function (err) {
        console.error('Pack Builder: cart add failed', err);
        alert(translate('cart_error'));
        if (btn) { btn.disabled = false; btn.textContent = translate('confirm_pack'); }
      });
  }

  /* ── Confetti ────────────────────────────────────────────────── */
  function fireConfetti() {
    var canvas = document.createElement('canvas');
    canvas.className = 'pb-confetti';
    canvas.width = 300;
    canvas.height = 200;
    document.body.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var particles = [];
    var colors = ['#1268f1', '#4caf50', '#ff9800', '#e53935', '#9c27b0', '#ffeb3b'];

    for (var i = 0; i < 60; i++) {
      particles.push({
        x: 150 + (Math.random() - 0.5) * 40,
        y: 180,
        vx: (Math.random() - 0.5) * 8,
        vy: -(Math.random() * 6 + 3),
        size: Math.random() * 5 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1,
      });
    }

    var frame = 0;
    (function animate() {
      frame++;
      ctx.clearRect(0, 0, 300, 200);
      var alive = false;
      particles.forEach(function (p) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.15;
        p.life -= 0.018;
        if (p.life <= 0) return;
        alive = true;
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size);
      });
      if (alive && frame < 120) requestAnimationFrame(animate);
      else canvas.remove();
    })();
  }

  /* ── Boot ────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
